import { FC } from 'react';

import { RevokedAccessSigner } from '../utils/fetch-revoked-access-signers.util';
import { AppFormattedMessage } from './app-formatted-message/index';
import Button from './button/index';
import { ModalWrapper } from './modal-wrapper/index';

const MAX_VISIBLE_REVOKED_SIGNERS = 3;

type SignatureRestoreAccessConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  signers: RevokedAccessSigner[];
};

export const SignatureRestoreAccessConfirmModal: FC<SignatureRestoreAccessConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  signers,
}) => {
  if (!isOpen) {
    return null;
  }

  const visibleSigners = signers.slice(0, MAX_VISIBLE_REVOKED_SIGNERS);
  const hiddenSignersCount = Math.max(0, signers.length - visibleSigners.length);

  return (
    <ModalWrapper
      className="centered z-[60] flex h-screen w-screen p-2"
      control={{
        modal: true,
        onOpenChange: (open) => !open && onClose(),
        open: isOpen,
      }}
      onBgClick={onClose}
      overlayClassName="z-[59]"
      side="CENTER"
    >
      <div className="flex w-full max-w-[485px] flex-col gap-6 rounded-lg bg-white p-6">
        <div className="flex items-start">
          <h2 className="text-xl font-[550] text-gray-700">
            <AppFormattedMessage id="SOME_SIGNERS_HAVE_REVOKED_ACCESS" />
          </h2>
        </div>

        <div className="flex flex-col gap-4 text-xs text-gray-500">
          <p>
            <AppFormattedMessage id="SIGNATURE_RESTORE_ACCESS_SIGNERS_INTRO" />
          </p>
          <div className="flex flex-col gap-1">
            {visibleSigners.map((signer) => (
              <span key={signer.id}>{signer.fullName}</span>
            ))}
            {hiddenSignersCount > 0 && (
              <span className="text-gray-500">
                {hiddenSignersCount === 1 ? (
                  <AppFormattedMessage id="SIGNATURE_RESTORE_ACCESS_OTHER" />
                ) : (
                  <AppFormattedMessage
                    id="SIGNATURE_RESTORE_ACCESS_OTHERS"
                    values={{ count: hiddenSignersCount }}
                  />
                )}
              </span>
            )}
          </div>
          <p>
            <AppFormattedMessage id="SIGNATURE_RESTORE_ACCESS_CONFIRM_WARNING" />
          </p>
        </div>

        <div className="mt-2 flex justify-end gap-3">
          <Button
            className="h-11 w-fit rounded border border-gray-300 text-sm"
            onClick={onClose}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-11 w-[238px] px-6 text-sm"
            onClick={onConfirm}
            styleType="BLUE_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="SEND_AND_RESTORE_ACCESS" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
